import { motion } from 'framer-motion'
import { Zap, TrendingUp, Brain, Target, Camera, MessageSquare, BarChart3, ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import TextSeedMorph from '../components/TextSeedMorph'

export default function AICoach() {
  const features = [
    {
      icon: Camera,
      title: '多摄像头自动录制',
      desc: '场馆部署 5-6 个海康摄像头，自动录制学员击球视频，智能切割精彩片段。',
    },
    {
      icon: Brain,
      title: '空间时序动作对比',
      desc: 'AI 提取骨骼点序列，与职业球员动作进行空间位置和时间序列的相似性对比。',
    },
    {
      icon: Target,
      title: '左右分屏对比视频',
      desc: '自动生成学员与职业球员的左右分屏对比视频，直观展示动作差异。',
    },
    {
      icon: TrendingUp,
      title: '多维度分析报告',
      desc: '挥拍速度、挥拍轨迹、击球时机、动力链传导等多维度评分与改进建议。',
    },
    {
      icon: MessageSquare,
      title: '个性化训练指导',
      desc: '针对动作薄弱环节，提供针对性训练建议，帮助初学者快速提升。',
    },
    {
      icon: BarChart3,
      title: '进步可视化追踪',
      desc: '记录每一次训练数据，生成进步曲线，直观看到自己的提升轨迹。',
    },
  ]

  const steps = [
    { step: '01', title: '摄像头录制', desc: '场馆 5-6 个海康摄像头自动录制学员击球' },
    { step: '02', title: 'AI 识别分析', desc: 'YOLOv8 提取骨骼点，智能切割击球片段' },
    { step: '03', title: '动作对比', desc: '空间时序相似性算法与职业球员动作对比' },
    { step: '04', title: '报告展示', desc: '生成分屏对比视频与多维度评分报告' },
  ]

  return (
    <div className="min-h-screen pt-16">
      {/* Hero */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[500px] opacity-15"
          style={{ background: 'radial-gradient(ellipse, rgba(59,130,246,0.5), transparent 70%)', filter: 'blur(60px)' }} />

        {/* 网球轮廓装饰（Pretext Irregular Wrapping 参考物） */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none z-0">
          <svg width="420" height="420" viewBox="0 0 420 420" fill="none" xmlns="http://www.w3.org/2000/svg"
            style={{ opacity: 0.055 }}>
            <circle cx="210" cy="210" r="200" stroke="#60a5fa" strokeWidth="1.5" />
            {/* 网球缝线弧 */}
            <path d="M 210 10 C 260 80, 260 340, 210 410" stroke="#60a5fa" strokeWidth="1.2" fill="none"/>
            <path d="M 210 10 C 160 80, 160 340, 210 410" stroke="#60a5fa" strokeWidth="1.2" fill="none"/>
            <path d="M 10 210 C 80 160, 340 160, 410 210" stroke="#60a5fa" strokeWidth="1.2" fill="none"/>
            <path d="M 10 210 C 80 260, 340 260, 410 210" stroke="#60a5fa" strokeWidth="1.2" fill="none"/>
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            {/* 顶部 badge */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium mb-8"
              style={{ background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)', color: '#60a5fa' }}
            >
              <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
              AI 驱动的新一代网球训练
            </motion.div>

            {/* ★ 文字种子聚合主标题 ★ */}
            <div className="mb-8 max-w-3xl mx-auto">
              <TextSeedMorph
                lines={['智拍AI网球', '用AI重新定义', '你的网球训练']}
                trigger="auto"
                fontSize={58}
                fontWeight="900"
                color="#ffffff"
                seedColor="rgba(96,165,250,0.7)"
              />
            </div>

            {/* 副标题淡入 */}
            <motion.p
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.8 }}
              className="text-xl text-gray-400 max-w-2xl mx-auto mb-8"
            >
              场馆场景：5-6个海康摄像头自动录制，AI 分析击球动作，与职业球员动作进行空间时序相似性对比，生成左右分屏对比视频和多维度分析报告。<br/>
              <span className="text-gray-500 text-sm">未部署城市支持手机拍摄上传</span>
            </motion.p>

            {/* CTA 按钮淡入 */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 2.2 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
            >
              <Link
                to="/#download"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-white transition-all duration-300 hover:gap-3"
                style={{ background: '#3b82f6', boxShadow: '0 0 30px rgba(59,130,246,0.4)' }}
              >
                立即体验
                <ArrowRight className="w-5 h-5" />
              </Link>
              <a
                href="#features"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-medium text-gray-300 transition-all"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
              >
                了解更多
              </a>
            </motion.div>
          </div>

          {/* Demo Video + Card 并排 */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-16 grid grid-cols-1 lg:grid-cols-5 gap-6 items-start max-w-5xl mx-auto"
          >
            {/* 宣传视频 */}
            <div className="lg:col-span-3">
              <div className="relative rounded-2xl overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, rgba(15,15,30,0.9), rgba(20,20,50,0.9))',
                  border: '1px solid rgba(96,165,250,0.2)',
                  boxShadow: '0 0 60px rgba(59,130,246,0.15), 0 40px 80px rgba(0,0,0,0.5)',
                }}
              >
                {/* 视频标题栏 */}
                <div className="flex items-center justify-between px-5 py-3 border-b border-white/5">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
                    <span className="text-xs text-gray-400 font-mono">产品宣传视频</span>
                  </div>
                  <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
                  </div>
                </div>
                <video
                  className="w-full aspect-video"
                  controls
                  autoPlay
                  muted
                  loop
                  playsInline
                  poster="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='640' height='360' viewBox='0 0 640 360'%3E%3Crect fill='%231a1a2e' width='640' height='360'/%3E%3Ccircle cx='320' cy='160' r='36' fill='none' stroke='%2360a5fa' stroke-width='3' opacity='0.6'/%3E%3Cpolygon points='310,145 310,175 340,160' fill='%2360a5fa' opacity='0.8'/%3E%3Ctext x='50%25' y='78%25' fill='%2360a5fa' text-anchor='middle' font-family='sans-serif' font-size='16' opacity='0.7'%3ELafit 智拍 AI 网球教练%3C/text%3E%3C/svg%3E"
                >
                    <source src="/videos/promo_h264.mp4" type="video/mp4" />
                  您的浏览器不支持视频播放
                </video>
              </div>
            </div>

            {/* 演示卡片 */}
            <div className="lg:col-span-2">
              <div className="relative rounded-2xl overflow-hidden h-full"
                style={{
                  background: 'linear-gradient(135deg, rgba(15,15,30,0.9), rgba(20,20,50,0.9))',
                  border: '1px solid rgba(96,165,250,0.2)',
                  boxShadow: '0 0 60px rgba(59,130,246,0.15), 0 40px 80px rgba(0,0,0,0.5)',
                }}
              >
                <div className="flex items-center justify-between px-5 py-3 border-b border-white/5">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                    <span className="text-xs text-gray-400 font-mono">AI 动作分析 · 实时</span>
                  </div>
                  <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
                  </div>
                </div>
                <div className="p-6 space-y-4">
                  <div className="space-y-3">
                    {[
                      { label: '正手击球', value: 92 },
                      { label: '反手击球', value: 85 },
                      { label: '发球', value: 78 },
                      { label: '身体协调', value: 91 },
                    ].map((item) => (
                      <div key={item.label}>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-gray-400">{item.label}</span>
                          <span className="font-mono font-medium text-blue-400">{item.value}分</span>
                        </div>
                        <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${item.value}%`, background: 'linear-gradient(90deg, #3b82f688, #3b82f6)' }} />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="p-3 rounded-xl" style={{ background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.15)' }}>
                    <div className="flex items-center gap-2 mb-2">
                      <Zap className="w-3.5 h-3.5 text-blue-400" />
                      <span className="text-xs text-blue-400 font-medium">DeepSeek AI 教练建议</span>
                    </div>
                    <p className="text-xs text-gray-400 leading-relaxed">
                      您的正手击球动作流畅度有显著提升，建议加强转体力量的传导...
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-24 relative">
        <div className="absolute inset-0 grid-bg opacity-10" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-black text-white mb-4">核心功能</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">AI + 计算机视觉 + 大语言模型，三重技术打造专业网球教练</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="p-6 rounded-2xl"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.04), rgba(255,255,255,0.01))',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: 'rgba(59,130,246,0.15)', color: '#60a5fa' }}>
                  <feature.icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="py-24 relative">
        <div className="absolute inset-0 grid-bg opacity-10" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-black text-white mb-4">使用流程</h2>
            <p className="text-gray-400">4 步完成 AI 动作分析</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {steps.map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl text-2xl font-black mb-4"
                  style={{ background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)', color: '#60a5fa' }}>
                  {item.step}
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                <p className="text-sm text-gray-400">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(59,130,246,0.1), rgba(139,92,246,0.1))' }} />
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-black text-white mb-6">
              让每一次挥拍都有 AI 教练指导
            </h2>
            <p className="text-gray-400 mb-8 max-w-xl mx-auto">
              扫码即用，无需下载。专业 AI 教练随时随地为您服务。
            </p>
            <Link
              to="/#download"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-white transition-all duration-300 hover:gap-3"
              style={{ background: '#3b82f6', boxShadow: '0 0 30px rgba(59,130,246,0.4)' }}
            >
              立即体验
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  )
}
